import { AddCircleIcon, GroupsIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import { Button } from '@components/Button'
import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import { PageColumn, PageHeader, PageScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import TripCard from '@components/TripCard'
import type { TripCreateSource } from '@orbit/api-types'
import { Trip } from '@types'
import React from 'react'
import { TripsGrid } from './TripsList.styles'
import JoinRequestCards from './JoinRequestCards'
import type { MyJoinRequestCard } from '@lib/joinRequests'
import { TestIds } from '@/testIds'
import { iconSizes } from '@/theme/tokens'
import { ErrorBanner } from '@components/Notice'

interface TripsListProps {
	trips: Trip[]
	loading: boolean
	error: string | null
	onTripClick: (trip: Trip) => void
	/** Opens trip creation, saying which button opened it. */
	onCreateTrip: (source: TripCreateSource) => void
	onJoinTrip: () => void
	/** My Join requests, the first cards in the grid. */
	joinRequests: MyJoinRequestCard[]
	onWithdrawRequest: (request: MyJoinRequestCard) => void
	withdrawingCode: string | null
}

const TripsList: React.FC<TripsListProps> = ({
	trips,
	loading,
	error,
	onTripClick,
	onCreateTrip,
	onJoinTrip,
	joinRequests,
	onWithdrawRequest,
	withdrawingCode,
}) => {
	if (loading) {
		return (
			<PageScroll>
				<PageLoader label="Loading your trips…" />
			</PageScroll>
		)
	}

	if (error) {
		return (
			<PageScroll>
				<PageColumn>
					<ErrorBanner>
						<strong>Error loading trips:</strong> {error}
					</ErrorBanner>
				</PageColumn>
			</PageScroll>
		)
	}

	// The empty state is for nothing at all: a pending request is already something to show (as on iOS).
	if (trips.length === 0 && joinRequests.length === 0) {
		return (
			<PageScroll>
				<EmptyState
					art="flights"
					title="No trips yet"
					description="Join a trip or create your own to get started."
					testId={TestIds.noTrip.screen}
				>
					<EmptyStateButton onClick={() => onCreateTrip('no_trips')} data-testid={TestIds.noTrip.addTripButton}>
						<AddCircleIcon sx={{ fontSize: iconSizes.lg }} />
						<span>Create Trip</span>
					</EmptyStateButton>
					<EmptyStateButton onClick={onJoinTrip}>
						<GroupsIcon sx={{ fontSize: iconSizes.lg }} />
						<span>Join Trip</span>
					</EmptyStateButton>
				</EmptyState>
			</PageScroll>
		)
	}

	return (
		<PageScroll>
			<PageColumn>
				<PageHeader
					title="My Trips"
					subtitle={countLabel(trips.length, 'trip')}
					actions={
						<>
							<Button onClick={() => onCreateTrip('trips_list')} data-testid={TestIds.trips.addButton}>
								<AddCircleIcon sx={{ fontSize: iconSizes.lg }} />
								<span>Create Trip</span>
							</Button>
							<Button onClick={onJoinTrip}>
								<GroupsIcon sx={{ fontSize: iconSizes.lg }} />
								<span>Join Trip</span>
							</Button>
						</>
					}
				/>

				<TripsGrid data-testid={TestIds.trips.list}>
					<JoinRequestCards
						requests={joinRequests}
						onWithdraw={onWithdrawRequest}
						withdrawingCode={withdrawingCode}
					/>
					{trips.map(trip => (
						<TripCard key={trip.id} trip={trip} onClick={onTripClick} />
					))}
				</TripsGrid>
			</PageColumn>
		</PageScroll>
	)
}

export default TripsList
